<!-- RemoteSelect.vue -->
<script setup lang="ts">
import { ref, reactive, onMounted, watch, PropType, computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, Array] as PropType<string | number | (string | number)[]>,
    default: null
  },
  api: {
    type: Function as PropType<(params: any) => Promise<any>>,
    required: true
  },
  onOptionsLoaded: {
    type: Function as PropType<(options: any[]) => void>,
    default: () => {}
  },
  searchKey: {
    type: String,
    default: 'keyword'
  },
  labelField: {
    type: String,
    default: 'label'
  },
  valueField: {
    type: String,
    default: 'value'
  },
  formatLabel: {
    type: Function as PropType<(item: any) => string>,
    default: (item: any) => item.label
  },
  multiple: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请选择'
  },
  echoItems: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  // 新增 prop
  valueType: {
    type: String,
    default: 'id',
    validator: (val: string) => ['id', 'object'].includes(val)
  }
})

const emit = defineEmits(['update:modelValue'])

// ✅ 关键：v-model 始终绑定 valueField（如 id）
const innerValue = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  }
})

// ✅ 新增：用于根据 value 查找完整对象（供父组件使用）
const selectedItemMap = ref<Record<string | number, any>>({})

const loading = ref(false)
const options = ref<any[]>([]) // 存原始 item 对象
const searchQuery = ref('')
const pagination = reactive({ page: 1, limit: 20, total: 0 })
const hasMore = ref(true)

const syncOptions = () => {
  props.onOptionsLoaded(options.value)
}

// 加载选项
const loadOptions = async (reset = false) => {
  if (reset) {
    pagination.page = 1
    options.value = []
    hasMore.value = true
  }

  loading.value = true
  try {
    const params: any = {
      page: pagination.page,
      limit: pagination.limit
    }
    if (searchQuery.value) {
      params[props.searchKey] = searchQuery.value
    }

    const res = await props.api(params)
    const newOptions = res.data || []

    // ✅ 构建 value -> item 的映射
    newOptions.forEach((item) => {
      selectedItemMap.value[item[props.valueField]] = item
    })

    if (reset) {
      options.value = newOptions
    } else {
      options.value.push(...newOptions)
    }

    pagination.total = res.total || 0
    hasMore.value = options.value.length < pagination.total
  } catch (e) {
    console.error('加载选项失败:', e)
  } finally {
    loading.value = false
    syncOptions()
  }
}

// 搜索处理
const handleSearch = (query: string) => {
  searchQuery.value = query
  loadOptions(true)
}

// 滚动加载
const handleScrollBottom = () => {
  if (hasMore.value && !loading.value) {
    pagination.page += 1
    loadOptions()
  }
}

// 预加载回显项
const preloadEchoItems = () => {
  if (!props.echoItems?.length) return

  props.echoItems.forEach((item) => {
    const key = item[props.valueField]
    if (!selectedItemMap.value[key]) {
      selectedItemMap.value[key] = item
    }
    const exists = options.value.some((opt) => opt[props.valueField] === key)
    if (!exists) {
      options.value.push(item)
    }
  })

  syncOptions()
}

onMounted(() => {
  loadOptions(true)
  preloadEchoItems()
})

watch(
  () => props.echoItems,
  () => {
    preloadEchoItems()
  },
  { deep: true }
)

// ✅ 暴露方法：让父组件能根据当前 value 获取完整对象
defineExpose({
  getSelectedItem: () => {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.map((v) => selectedItemMap.value[v]).filter(Boolean)
    }
    return selectedItemMap.value[props.modelValue] || null
  },
  reload: () => loadOptions(true),
  clear: () => {
    options.value = []
    selectedItemMap.value = {}
    searchQuery.value = ''
    pagination.page = 1
    hasMore.value = true
  }
})
</script>

<template>
  <el-select
    v-model="innerValue"
    :placeholder="placeholder"
    :loading="loading"
    :multiple="multiple"
    filterable
    remote
    :remote-method="handleSearch"
    @scroll-bottom="handleScrollBottom"
    @visible-change="(visible) => !visible && (searchQuery = '')"
    style="width: 100%; min-height: 40px"
  >
    <el-option
      v-for="item in options"
      :key="item[props.valueField]"
      :label="formatLabel(item)"
      :value="props.valueType === 'object' ? item : item[props.valueField]"
    />
  </el-select>
</template>
